import React, { useEffect, useMemo } from "react";
import { Popover, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

/* ---------------- TYPES ---------------- */

type HoverCardVariant2 =
  | "contactleadOwner"
  | "leadOwner"
  | "dealOwner"
  | "quoteOwner"
  | "leadSource"
  | "contactleadSource"
   | "leadGivenBy"           // ← new one
  | "contactleadGivenBy"; 

type SubmoduleHoverCardProps = {
  name?: React.ReactNode;
  role?: string;
  contactleadowner_name?: string;
  leadowner_name?: string;
  dealowner_name?: string;
  quoteowner_name?: string;
  company_name?: string;
  leadsource_name?: string;
  lead_source_name?: string;
  createdAt?: string;
  email?: string;        // ✅ OPTIONAL
  phone?: string; 

  /** Controls behavior per page */
  variant2?: HoverCardVariant2 | HoverCardVariant2[];
};

/* ---------------- DATE FORMATTER ---------------- */

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const time = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) return `Today, ${time}`;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ---------------- COMPONENT ---------------- */

const SubmoduleHoverCard = ({
  name,
  role,
  contactleadowner_name,
  leadowner_name,
  dealowner_name,
  quoteowner_name,
  company_name,
  leadsource_name,
  lead_source_name,
  createdAt,
  email,         
  phone,
  variant2 = [],
}: SubmoduleHoverCardProps) => {
  const navigate = useNavigate();

  /* ---------- Shine Animation ---------- */
  useEffect(() => {
    const styleId = "hover-card-shine";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        @keyframes shine {
          0% { transform: skewX(-20deg) translateX(-150%); }
          50% { transform: skewX(-20deg) translateX(150%); }
          100% { transform: skewX(-20deg) translateX(150%); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  /* ---------- Avatar Initial ---------- */
  const initial = useMemo(
    () => (typeof name === "string" ? name.charAt(0).toUpperCase() : ""),
    [name]
  );

  const formattedDate = useMemo(
    () => (createdAt ? formatDateTime(createdAt) : ""),
    [createdAt]
  );

  /* ---------- ACTIONS (SUPPORT 2 OR 3 BUTTONS) ---------- */
  const actions = useMemo(() => {
    const variants = Array.isArray(variant2) ? variant2 : [variant2];

    return variants
      .map((v) => {
        switch (v) {
          case "contactleadOwner":
            return {
              label: "View Contacts",
              onNavigate: () =>
                navigate(
                  `/crm/contact-list?contactleadowner=${encodeURIComponent(
                    contactleadowner_name || ""
                  )}&company=${encodeURIComponent(company_name || "")}`
                ),
            };

          case "leadOwner":
            return {
              label: "View Leads",
              onNavigate: () =>
                navigate(
                  `/leads?owner=${encodeURIComponent(
                    leadowner_name || ""
                  )}`
                ),
            };

          case "dealOwner":
            return {
              label: "View Deals",
              onNavigate: () =>
                navigate(
                  `/crm/deals?owners=${encodeURIComponent(
                    dealowner_name || ""
                  )}`
                ),
            };
          case "quoteOwner":
            return {
              label: "View Quotes",
              onNavigate: () =>
                navigate(
                  `/crm/proposals-list?owners=${encodeURIComponent(
                    quoteowner_name || ""
                  )}`
                ),
            };
case "leadGivenBy":
  return {
    label: "View Leads",
    onNavigate: () =>
      navigate(
        `/leads?givenby=${encodeURIComponent(leadowner_name || "")}`
      ),
    buttonText: "View Leads for this Given By →",
    titleHint: "View leads given by this person/source",
  };
            case "leadSource":
        return {
          label: "View Leads",
          onNavigate: () =>
            navigate(
              `/leads?source=${encodeURIComponent(lead_source_name || "")}`
            ),
          buttonText: "View Leads for this Lead Source →",
          titleHint: "View leads for this source",
        };

            case "contactleadSource":
        return {
          label: "View Contacts",
          onNavigate: () =>
            navigate(
              `/crm/contact-list?source=${encodeURIComponent(leadsource_name || "")}`
            ),
          buttonText: "View Contacts for this Lead Source →",
          titleHint: "View contacts for this source",
        };

          default:
            return null;
        }
      })
      .filter(Boolean) as {
      label: string;
      onNavigate: () => void;
    }[];
  }, [
    variant2,
    navigate,
    contactleadowner_name,
    leadowner_name,
    dealowner_name,
    leadsource_name,
    quoteowner_name,
    company_name,
  ]);

  /* ---------- CARD CONTENT ---------- */
  const content = (
    <div style={{ width: 260 }}>
      {/* Header */}
      <div className="d-flex align-items-center mb-2">
        <Avatar
          shape="square"
          className="btn btn-primary"
          style={{ width: 36, height: 36, fontWeight: 600 }}
        >
          {initial || <UserOutlined />}
        </Avatar>

        <div className="ms-2">
          <div
  className="fw-semibold text-decoration-underline text-truncate"
  style={{
    cursor: "pointer",
    maxWidth: 160,        // 👈 controls truncation width
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }}
  title={typeof name === "string" ? name : undefined} // optional tooltip
  onClick={(e) => {
    e.stopPropagation();
    actions[0]?.onNavigate();
  }}
>
  {name || "Unassigned"}
</div>

          <div className="text-muted small">{role || "-"}</div>
        </div>
      </div>

      {/* Email */}
{email && (
  <div className="d-flex align-items-center small mb-1">
    <i className="ti ti-mail me-2" />
    <span className="text-truncate" style={{ maxWidth: 200 }}>
      {email}
    </span>
  </div>
)}

{/* Phone */}
{phone && (
  <div className="d-flex align-items-center small mb-1">
    <i className="ti ti-phone me-2" />
    <span>{phone}</span>
  </div>
)}


      {/* Date */}
      {formattedDate && (
        <div className="d-flex align-items-center mb-1 small">
          <i className="ti ti-calendar-time me-2" />
          {formattedDate}
        </div>
      )}

      {/* ---------- 2 BUTTONS ---------- */}
      {actions.length === 2 && (
  <div className="d-flex gap-2 mt-2">
    {actions.map((action, index) => (
      <button
        key={index}
        className={`btn btn-sm ${
          index === 0 ? "btn-primary" : "btn-outline-secondary"
        } w-100`}
        onClick={(e) => {
          e.stopPropagation();
          action.onNavigate();
        }}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <span style={{ position: "relative", zIndex: 2 }}>
          {action.label}
        </span>

        {/* Shine animation */}
        <span
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%)",
            transform: "skewX(-20deg) translateX(-150%)",
            animation: "shine 3s infinite",
          }}
        />
      </button>
    ))}
  </div>
)}


      {/* ---------- 3 BUTTONS ---------- */}
      {actions.length >= 3 && (
  <>
    <div className="d-flex gap-2 mt-2">
      {/* BUTTON 1 */}
      <button
        className="btn btn-sm btn-primary w-100"
        onClick={(e) => {
          e.stopPropagation();
          actions[0].onNavigate();
        }}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <span style={{ position: "relative", zIndex: 2 }}>
          {actions[0].label}
        </span>
        <span
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%)",
            transform: "skewX(-20deg) translateX(-150%)",
            animation: "shine 3s infinite",
          }}
        />
      </button>

      {/* BUTTON 2 */}
      <button
        className="btn btn-sm btn-outline-secondary w-100"
        onClick={(e) => {
          e.stopPropagation();
          actions[1].onNavigate();
        }}
        style={{ position: "relative", overflow: "hidden" }}
      >
        <span style={{ position: "relative", zIndex: 2 }}>
          {actions[1].label}
        </span>
        <span
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(to right, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%)",
            transform: "skewX(-20deg) translateX(-150%)",
            animation: "shine 3s infinite",
          }}
        />
      </button>
    </div>

    {/* BUTTON 3 */}
    <button
      className="btn btn-sm btn-outline-success w-100 mt-2"
      onClick={(e) => {
        e.stopPropagation();
        actions[2].onNavigate();
      }}
      style={{ position: "relative", overflow: "hidden" }}
    >
      <span style={{ position: "relative", zIndex: 2 }}>
        {actions[2].label}
      </span>
      <span
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to right, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%)",
          transform: "skewX(-20deg) translateX(-150%)",
          animation: "shine 3s infinite",
        }}
      />
    </button>
  </>
)}

    </div>
  );

  /* ---------- TRIGGER ---------- */
  return (
    <Popover content={content} placement="right" trigger="hover">
      <span
  role="button"
  tabIndex={0}
  className="text-truncate d-inline-block"
  style={{
    cursor: "pointer",
    maxWidth: 140,        // 👈 table column width
    verticalAlign: "middle",
  }}
  title={typeof name === "string" ? name : undefined}
  onClick={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}
>
  {name || "Unassigned"}
</span>

    </Popover>
  );
};

export default SubmoduleHoverCard;
